import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-final-form';
import { get } from 'lodash';
import { doPostQCXRequest } from '../../../utils/api/domain/domain-api-utils';
import QCXFormTitle from '../../form-title/QCXFormTitle';
import QCXAtributosTratamentoTributarioDuimpV3 from '../../../shared-components/atributos-tratamento-tributario-duimp/QCXAtributosTratamentoTributarioDuimpV3';

export default function RepassarTratamentosTributariosObrigatoriosTab({
  values = {},
  disabled = false,
  isActive = true,
}) {
  const form = useForm();
  const [listTratamentosObrigatorios, setListTratamentosObrigatorios] = useState([]);
  const ultimaBuscaMercadoriasKeyRef = useRef(null);

  const mercadoriaIdsSelecionadas = useMemo(
    () =>
      (Array.isArray(values?.meta?.selected) ? values.meta.selected : [])
        .map((mercadoria) => Number(mercadoria?.id))
        .filter((mercadoriaId) => Number.isInteger(mercadoriaId)),
    [values?.meta?.selected]
  );
  const mercadoriaIdsKey = mercadoriaIdsSelecionadas.join(',');

  const gruposTratamentosObrigatorios = useMemo(() => {
    const adicoesSelecionadas = new Map(
      (Array.isArray(values?.meta?.selected) ? values.meta.selected : []).map((mercadoria) => [
        Number(mercadoria?.idAdicao),
        {
          id: Number(mercadoria?.idAdicao),
          numero: mercadoria?.nrAdicao || '-',
        },
      ])
    );
    const grupos = new Map();

    listTratamentosObrigatorios.forEach((adicao) => {
      const adicaoId = Number(adicao?.adicaoId);
      const adicaoSelecionada = adicoesSelecionadas.get(adicaoId) || { id: adicaoId, numero: '-' };

      (adicao?.tratamentos || []).forEach((item) => {
        const tratamento = item?.tratamentos;
        if (!tratamento) return;

        const key =
          tratamento.codeTratamento ||
          `${tratamento?.tributo?.codigo};${tratamento?.regime?.codigo};${tratamento?.fundamentoLegal?.codigo}`;
        let grupo = grupos.get(key);

        if (!grupo) {
          grupo = {
            key,
            tratamento,
            adicoes: new Map(),
            atributos: new Map(),
          };
          grupos.set(key, grupo);
        }

        grupo.adicoes.set(adicaoId, adicaoSelecionada);

        (tratamento.atributos || []).forEach((atributo) => {
          let atributoAgrupado = grupo.atributos.get(atributo.codigo);

          if (!atributoAgrupado) {
            atributoAgrupado = {
              ...atributo,
              dominios: [...(atributo.dominios || [])],
              adicoes: new Set(),
            };
            grupo.atributos.set(atributo.codigo, atributoAgrupado);
          } else {
            const dominiosExistentes = new Set(
              (atributoAgrupado.dominios || []).map((dominio) => dominio.valor)
            );
            (atributo.dominios || []).forEach((dominio) => {
              if (!dominiosExistentes.has(dominio.valor)) {
                atributoAgrupado.dominios.push(dominio);
              }
            });
          }

          atributoAgrupado.adicoes.add(adicaoId);
        });
      });
    });

    return Array.from(grupos.values()).map((grupo) => ({
      ...grupo,
      adicoes: Array.from(grupo.adicoes.values()).sort((a, b) => {
        const numeroA = Number(a.numero);
        const numeroB = Number(b.numero);
        if (Number.isFinite(numeroA) && Number.isFinite(numeroB)) return numeroA - numeroB;
        return a.id - b.id;
      }),
      atributos: Array.from(grupo.atributos.values()).map((atributo) => ({
        ...atributo,
        adicoes: Array.from(atributo.adicoes),
      })),
    }));
  }, [listTratamentosObrigatorios, values?.meta?.selected]);

  const blocosPorAdicoes = useMemo(() => {
    const blocos = new Map();

    gruposTratamentosObrigatorios.forEach((grupo) => {
      const atributosPorAdicoes = new Map();

      grupo.atributos.forEach((atributo) => {
        const chaveAdicoes = atributo.adicoes.slice().sort((a, b) => a - b).join(',');
        if (!atributosPorAdicoes.has(chaveAdicoes)) atributosPorAdicoes.set(chaveAdicoes, []);
        atributosPorAdicoes.get(chaveAdicoes).push(atributo);
      });

      atributosPorAdicoes.forEach((atributos, chaveAdicoes) => {
        if (!blocos.has(chaveAdicoes)) {
          blocos.set(chaveAdicoes, {
            key: chaveAdicoes,
            adicoes: chaveAdicoes.split(',').map(Number).filter(Number.isInteger),
            tratamentos: [],
          });
        }

        blocos.get(chaveAdicoes).tratamentos.push({
          key: grupo.key,
          ...grupo.tratamento,
          atributos,
          tributos: [{ chave: 0, tratamentos: { ...grupo.tratamento, atributos } }],
        });
      });
    });

    return Array.from(blocos.values());
  }, [gruposTratamentosObrigatorios]);

  useEffect(() => {
    if (!isActive) return undefined;

    const valoresAtuais = form.getState().values;

    blocosPorAdicoes.forEach((bloco) => {
      const adicaoBase = bloco.adicoes[0];
      const basePath = `ignorableFields.tratamentosTtce.metadados.valoresPorAdicao.adicao_${adicaoBase}`;

      bloco.tratamentos
        .flatMap((tratamento) => tratamento.atributos)
        .forEach((atributo) => {
          const valor = get(valoresAtuais, `${basePath}.${atributo.codigo}`);

          bloco.adicoes.slice(1).forEach((adicaoId) => {
            const caminhoDestino = `ignorableFields.tratamentosTtce.metadados.valoresPorAdicao.adicao_${adicaoId}.${atributo.codigo}`;
            const valorDestino = get(valoresAtuais, caminhoDestino);

            if (JSON.stringify(valorDestino) !== JSON.stringify(valor)) {
              form.change(caminhoDestino, valor);
            }
          });
        });
    });

    return undefined;
  }, [blocosPorAdicoes, form, isActive, values]);

  useEffect(() => {
    if (!isActive) {
      ultimaBuscaMercadoriasKeyRef.current = null;
      return undefined;
    }

    if (ultimaBuscaMercadoriasKeyRef.current === mercadoriaIdsKey) return undefined;

    ultimaBuscaMercadoriasKeyRef.current = mercadoriaIdsKey;
    let isMounted = true;

    const buscarTratamentosObrigatorios = async () => {
      try {
        const response = await doPostQCXRequest(
          ['diduimp', 'auxiliar', 'buscar-tratamentos-tributarios-obrigatorios'],
          mercadoriaIdsKey ? mercadoriaIdsKey.split(',').map(Number) : []
        );

        if (isMounted) {
          const tratamentosObrigatorios = Array.isArray(response?.data) ? response.data : [];
          setListTratamentosObrigatorios((tratamentosAtuais) =>
            JSON.stringify(tratamentosAtuais) === JSON.stringify(tratamentosObrigatorios)
              ? tratamentosAtuais
              : tratamentosObrigatorios
          );

          const adicoesIds = new Set(
            tratamentosObrigatorios.map((adicao) => `adicao_${adicao?.adicaoId}`)
          );
          const tratamentosPorAdicao = Object.fromEntries(
            tratamentosObrigatorios.map((adicao) => [
              `adicao_${adicao?.adicaoId}`,
              adicao?.tratamentos || [],
            ])
          );
          const valoresPorAdicao =
            form.getState().values?.ignorableFields?.tratamentosTtce?.metadados?.valoresPorAdicao || {};
          const valoresPorAdicaoAtualizados = Object.fromEntries(
            Object.entries(valoresPorAdicao).filter(([adicaoId]) => adicoesIds.has(adicaoId))
          );

          const valoresPorAdicaoAtuais =
            form.getState().values?.ignorableFields?.tratamentosTtce?.metadados?.valoresPorAdicao || {};
          const tratamentosPorAdicaoAtuais =
            form.getState().values?.ignorableFields?.tratamentosTtce?.metadados?.tratamentosPorAdicao || {};

          if (JSON.stringify(valoresPorAdicaoAtuais) !== JSON.stringify(valoresPorAdicaoAtualizados)) {
            form.change(
              'ignorableFields.tratamentosTtce.metadados.valoresPorAdicao',
              valoresPorAdicaoAtualizados
            );
          }
          if (JSON.stringify(tratamentosPorAdicaoAtuais) !== JSON.stringify(tratamentosPorAdicao)) {
            form.change(
              'ignorableFields.tratamentosTtce.metadados.tratamentosPorAdicao',
              tratamentosPorAdicao
            );
          }
        }
      } catch (error) {
        console.error('Erro ao buscar tratamentos tributários obrigatórios.', error);
        if (isMounted) setListTratamentosObrigatorios([]);
      }
    };

    buscarTratamentosObrigatorios();

    return () => {
      isMounted = false;
    };
  }, [form, isActive, mercadoriaIdsKey]);

  if (!isActive) return null;

  return (
    <>
      {blocosPorAdicoes.map((bloco) => {
        const numerosAdicoes = bloco.adicoes.map((adicaoId) => {
          const grupo = gruposTratamentosObrigatorios
            .flatMap((tratamento) => tratamento.adicoes)
            .find((adicao) => adicao.id === adicaoId);
          return grupo?.numero || adicaoId;
        });
        const tituloAdicoes =
          numerosAdicoes.length > 1 ? `Adições ${numerosAdicoes.join('/')}` : `Adição ${numerosAdicoes[0]}`;

        return (
          <section key={bloco.key}>
            <QCXFormTitle title={tituloAdicoes} />
            {bloco.tratamentos.map((tratamento) => (
              <QCXAtributosTratamentoTributarioDuimpV3
                key={`${bloco.key}-${tratamento.key}`}
                name={`ignorableFields.tratamentosTtce.metadados.valoresPorAdicao.adicao_${bloco.adicoes[0]}`}
                tributos={tratamento.tributos}
                isConsult={disabled}
                showTitle={false}
              />
            ))}
          </section>
        );
      })}
    </>
  );
}
